import * as React from 'react';
import {
  Box,
  Divider,
  Grid,
  Button,
  Avatar,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import Alert from '@material-ui/lab/Alert';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';

import useEventListener from '../../lib/useEventListener';
import deselectElementPreventDefaultAndBlur from '../../lib/deselectAndBlur';
import globalUseStyles from '../../globalClasses';
import EstimatorCommentCards from '../estimator/EstimatorCommentCards';
import JewelerSidebar from '../jeweler/JewelerSidebar';
import JewelerRepairItemDisplay from '../jeweler/JewelerRepairItemDisplay';
import type { IScannedRepair } from '../marking/MarkingInterfaces';
import RepairCardCarousel from '../metalCollection/RepairCardCarousel';
import RejectWorkDialog from '../production/RejectWorkDialog';

interface SidebarRepair {
  barcode: string;
  secondaryText: string;
  status: 'shipLog' | 'scheduled';
  secondaryStatusStart: Date | null;
}

const useStyles = makeStyles((theme) => createStyles({
  resourceButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

const Watches: React.FC = () => {
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedBarcode, setSelectedBarcode] = React.useState<string>();
  const [repairId, setRepairId] = React.useState<undefined | string>();
  const [showRejectWorkDialog, setShowRejectWorkDialog] = React.useState<boolean>(false);
  const [rejectComment, setComment] = React.useState<string>('');
  const classes = useStyles();

  const getRepairId = (repairBarcodeParam: string) => {
    axios.get<{ repairId: string }>('/watches/ScanRepair', {
      params: {
        barcode: repairBarcodeParam,
      },
    })
      .then((response) => { setRepairId(response.data.repairId); return response.data.repairId; })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const binRepairFetcher = (url: string) => axios.get<{ repairs: SidebarRepair[] }>(url).then((res) => res.data.repairs)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data: binRepairs, mutate } = useSWR('/watches/GetBinRepairs', binRepairFetcher);

  const updateSelectedRepair = (repairBarcodeParam: string) => {
    if (binRepairs?.filter((r) => r.status === 'shipLog').length === 0
      || binRepairs?.filter((r) => r.status === 'shipLog')?.findIndex((r) => r.barcode === repairBarcodeParam) !== -1
    ) {
      getRepairId(repairBarcodeParam);
      setSelectedBarcode(repairBarcodeParam);
    } else {
      enqueueSnackbar('Please scan ship log items first', { persist: false, variant: 'error' });
    }
  };

  const repairFetcher = (url: string, repairIdParam: string) => axios.get<IScannedRepair>(url, {
    params: {
      repairId: repairIdParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const { data } = useSWR(repairId ? ['/watches/GetRepair', repairId] : null, (url, repairIdParam) => repairFetcher(url, repairIdParam));

  const startSoak = () => {
    axios.post<string>('/watches/startSoak', {
      barcode: selectedBarcode,
    }).then(() => {
      mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      setRepairId(undefined);
      setSelectedBarcode(undefined);
    }).catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });
  };

  const completeWork = () => {
    axios.post<string>('/watches/completeWork', {
      barcode: selectedBarcode,
    }).then(() => {
      mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      setRepairId(undefined);
      setSelectedBarcode(undefined);
    }).catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });
  };

  const rejectWork = (rejectReason: string[]) => {
    axios.post('/watches/rejectWork', {
      barcode: selectedBarcode,
      RejectReasons: rejectReason,
      comment: rejectComment,
    })
      .then(() => {
        setRepairId(undefined);
        setSelectedBarcode(undefined);
        setComment('');
        mutate().catch((e: AxiosError) => { enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' }); });
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      });
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        deselectElementPreventDefaultAndBlur(e);
        updateSelectedRepair(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        deselectElementPreventDefaultAndBlur(e);
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        deselectElementPreventDefaultAndBlur(e);
        completeWork();
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  return (
    <Grid container spacing={1}>
      <Grid item xs={2} style={{ minWidth: '15rem' }}>
        <JewelerSidebar
          sidebarRepairs={binRepairs || []}
          repairItemOnClick={process.env.REACT_APP_ENV === 'production' ? () => { } : updateSelectedRepair}
          selectedBarcode={selectedBarcode}
          secondaryStatusName="Paused"
        />
      </Grid>
      <Grid item xs={10}>
        <Box px={1}>
          <Divider variant="fullWidth" orientation="vertical" />
        </Box>
        <Grid container item spacing={2} sm={12} md={11}>
          <Grid item xs={12}>
            <Alert
              severity="info"
            >
              {repairId ? 'Review images and repair information.' : 'Scan in repair item to start the watch repair process.'}
            </Alert>
          </Grid>
          {repairId && (
            <>
              <Grid item xs={12}>
                <RepairCardCarousel
                  key={selectedBarcode}
                  barcode={selectedBarcode || ''}
                  secondaryText={
                    `${data?.repairItems?.length || ''} item${data?.repairItems?.length === 1 ? '' : 's'}`
                  }
                  hideArrows
                />
              </Grid>
              <Grid item>
                <Button onClick={startSoak} color="primary" variant="contained">{'Pause for Regulation'}</Button>
              </Grid>
              <Grid item>
                <Button color="secondary" variant="contained" onClick={() => { setShowRejectWorkDialog(true); }}>
                  <Box mr={1}>
                    <Avatar className={clsx(globalStyles.smallAvatar, globalStyles.redIconReverse)}>
                      <BrokenImageIcon className={globalStyles.smallIconSize} />
                    </Avatar>
                  </Box>
                  {'Watch Part Issue'}
                </Button>
              </Grid>
              {/* TODO: Below buttons will be replaced by dynamically generated buttons with links to a page that provides information about the specific watch. */}
              <Grid item container xs={12} spacing={2}>
                <Grid item>
                  <Button
                    id="resource-button-1"
                    className={classes.resourceButton}
                    variant="contained"
                  >
                    {'Resource Name'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    id="resource-button-2"
                    className={classes.resourceButton}
                    variant="contained"
                  >
                    {'Resource Name'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    id="resource-button-3"
                    className={classes.resourceButton}
                    variant="contained"
                  >
                    {'Resource Name'}
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <JewelerRepairItemDisplay repairItems={data?.repairItems || []} showExtraProps />
              </Grid>
              <Grid item xs={12}>
                <EstimatorCommentCards
                  repairId={repairId}
                  repairTableLength={0}
                  allowNewComments
                />
              </Grid>
              <Grid item xs={12} />
              <Grid item xs={12} container direction="row-reverse">
                <Grid item>
                  <Box py={1}>
                    <Button
                      id="complete-work-button"
                      color="primary"
                      variant="contained"
                      onClick={() => completeWork()}
                    >
                      {'Complete Work'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <RejectWorkDialog
                barcode={selectedBarcode || ''}
                showModal={showRejectWorkDialog}
                onClose={() => setShowRejectWorkDialog(false)}
                rejectWork={rejectWork}
                station="Watches"
                setRejectComment={setComment}
              />
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Watches;
